import { useEffect } from "react";
import { ViewMode } from "../../types";
import { SqButton } from "@/components/squircle-ui/button";
import {
  SqTooltipProvider,
  SqTooltip,
  SqTooltipTrigger,
  SqTooltipContent,
} from "@/components/squircle-ui/tooltip";
import {
  RemixiconComponentType,
  RiLandscapeLine,
  RiPieChartLine,
  RiTableLine,
} from "@remixicon/react";

interface Props {
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
  metadata?: Partial<
    Record<
      ViewMode,
      {
        hide?: boolean;
        disabled?: boolean;
        disablingReason?: string;
      }
    >
  >;
}

const ViewToggle = ({ view, onViewChange, metadata }: Props) => {
  useEffect(() => {
    if (
      (view === "table_view" && metadata?.table_view?.disabled) ||
      (view === "line_chart_view" && metadata?.line_chart_view?.disabled) ||
      (view === "creative_view" && metadata?.creative_view?.disabled)
    ) {
      if (!metadata?.table_view?.disabled) onViewChange("table_view");
      if (!metadata?.creative_view?.disabled) onViewChange("creative_view");
      if (!metadata?.line_chart_view?.disabled) onViewChange("line_chart_view");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    metadata?.table_view?.disabled,
    metadata?.line_chart_view?.disabled,
    metadata?.creative_view?.disabled,
  ]);

  const renderViewButton = ({
    view,
    label,
    Icon,
    disabled,
    disablingReason,
    isSelected,
  }: {
    view: ViewMode;
    label: string;
    Icon: RemixiconComponentType;
    disabled: boolean;
    disablingReason?: string;
    isSelected: boolean;
  }) => {
    if (disabled && disablingReason)
      return (
        <SqTooltipProvider key={view}>
          <SqTooltip>
            <SqTooltipTrigger asChild>
              <span tabIndex={0} className="h-6 w-6 flex items-center justify-center">
                <SqButton
                  className="h-6 w-6 p-0"
                  onClick={() => onViewChange(view)}
                  bgColor={isSelected ? "#FF450014" : "white"}
                  hoverBgColor={isSelected ? "#FF450024" : "white"}
                  disabled
                  radius={4}
                >
                  <Icon
                    size={14}
                    className={`${isSelected ? "text-brand-orange" : "text-[#9AA3BD]"} shrink-0`}
                  />
                </SqButton>
              </span>
            </SqTooltipTrigger>
            <SqTooltipContent side="bottom" align="start" sideOffset={5} portal={true}>
              {disablingReason}
            </SqTooltipContent>
          </SqTooltip>
        </SqTooltipProvider>
      );
    return (
      <SqTooltipProvider key={view}>
        <SqTooltip>
          <SqTooltipTrigger asChild>
            <span tabIndex={0} className="h-6 w-6 flex items-center justify-center">
              <SqButton
                className="h-6 w-6 p-0 shrink-0"
                onClick={() => onViewChange(view)}
                disabled={disabled}
                bgColor={isSelected ? "#FF450014" : "white"}
                radius={4}
              >
                <Icon
                  size={14}
                  className={`${isSelected ? "text-brand-orange" : "text-[#9AA3BD]"} shrink-0`}
                />
              </SqButton>
            </span>
          </SqTooltipTrigger>
          <SqTooltipContent side="bottom" align="start" sideOffset={5} portal={true}>
            {label}
          </SqTooltipContent>
        </SqTooltip>
      </SqTooltipProvider>
    );
  };

  return (
    <div
      className="relative flex gap-1 p-1 squircle squircle-fill-[white] drop-shadow-sm w-fit h-fit
     before:absolute before:inset-0 before:squircle before:squircle-radius-[8px] 
     before:squircle-outline-[1px] before:squircle-outline-color-border-gray 
     before:pointer-events-none"
    >
      {!metadata?.table_view?.hide &&
        renderViewButton({
          view: "table_view",
          label: "Table View",
          Icon: RiTableLine,
          disabled: !!metadata?.table_view?.disabled,
          disablingReason: metadata?.table_view?.disablingReason,
          isSelected: view === "table_view",
        })}
      {!metadata?.creative_view?.hide &&
        renderViewButton({
          view: "creative_view",
          label: "Creative View",
          Icon: RiLandscapeLine,
          disabled: !!metadata?.creative_view?.disabled,
          disablingReason: metadata?.creative_view?.disablingReason,
          isSelected: view === "creative_view",
        })}
      {!metadata?.line_chart_view?.hide &&
        renderViewButton({
          view: "line_chart_view",
          label: "Line Chart View",
          Icon: RiPieChartLine,
          disabled: !!metadata?.line_chart_view?.disabled,
          disablingReason: metadata?.line_chart_view?.disablingReason,
          isSelected: view === "line_chart_view",
        })}
    </div>
  );
};
export default ViewToggle;
